// Simple Web Audio Synthesizer for Casino Sounds

class CasinoAudioEngine {
  private ctx: AudioContext | null = null;
  private enabled = true;

  init() {
    if (!this.ctx) {
      this.ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
  }

  toggle(enabled: boolean) {
    this.enabled = enabled;
  }

  playDrop() {
    if (!this.enabled || !this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.connect(gain);
    gain.connect(this.ctx.destination);

    const now = this.ctx.currentTime;
    osc.type = 'sine';
    
    // Quick pitch drop
    osc.frequency.setValueAtTime(600, now);
    osc.frequency.exponentialRampToValueAtTime(100, now + 0.1);
    
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(0.8, now + 0.02); // Louder
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);

    osc.start(now);
    osc.stop(now + 0.1);
  }

  playBounce() {
    if (!this.enabled || !this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.connect(gain);
    gain.connect(this.ctx.destination);

    const now = this.ctx.currentTime;
    osc.type = 'sine';
    
    // Light, slim, glass-like tick
    osc.frequency.setValueAtTime(1200, now);
    osc.frequency.exponentialRampToValueAtTime(800, now + 0.02);
    
    gain.gain.setValueAtTime(0.1, now); // Much quieter for multiple balls
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.02);

    osc.start(now);
    osc.stop(now + 0.02);
  }

  playWin(mult: number) {
    if (!this.enabled || !this.ctx) return;
    
    if (mult < 1) {
      this.playLoss();
    } else if (mult < 10) {
      this.playMediumWin();
    } else {
      this.playBigWin();
    }
  }

  private playLoss() {
    if (!this.ctx) return;
    const osc = this.ctx.createOscillator();
    const gain = this.ctx.createGain();
    osc.connect(gain);
    gain.connect(this.ctx.destination);

    const now = this.ctx.currentTime;
    osc.type = 'sine';
    osc.frequency.setValueAtTime(200, now);
    osc.frequency.linearRampToValueAtTime(150, now + 0.2);
    
    gain.gain.setValueAtTime(0.6, now); // Louder
    gain.gain.linearRampToValueAtTime(0, now + 0.2);

    osc.start(now);
    osc.stop(now + 0.2);
  }

  private playMediumWin() {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    
    // Play a nice major chord (C E G)
    [523.25, 659.25, 783.99].forEach((freq, i) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.connect(gain);
      gain.connect(this.ctx!.destination);

      osc.type = 'sine';
      osc.frequency.value = freq;
      
      const start = now + (i * 0.05); // slight arpeggio
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.6, start + 0.05); // Louder
      gain.gain.exponentialRampToValueAtTime(0.01, start + 0.5);

      osc.start(start);
      osc.stop(start + 0.5);
    });
  }

  private playBigWin() {
    if (!this.ctx) return;
    const now = this.ctx.currentTime;
    
    // Triumphant fast arpeggio ascending 4 octaves
    const notes = [
      261.63, 329.63, 392.00, // C4 E4 G4
      523.25, 659.25, 783.99, // C5 E5 G5
      1046.50, 1318.51, 1567.98, // C6 E6 G6
      2093.00 // C7
    ];

    notes.forEach((freq, i) => {
      const osc = this.ctx.createOscillator();
      const gain = this.ctx.createGain();
      osc.connect(gain);
      gain.connect(this.ctx!.destination);

      osc.type = 'square'; // chiptune feel
      osc.frequency.value = freq;
      
      const start = now + (i * 0.08); 
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.4, start + 0.02); // Louder
      gain.gain.exponentialRampToValueAtTime(0.01, start + 0.2);

      osc.start(start);
      osc.stop(start + 0.2);
    });
  }
}

export const audioEngine = new CasinoAudioEngine();
